/**
 * AUTHOR REGISTRY — single source of truth for bylines.
 * ----------------------------------------------------------------------------
 * A May 2026 AI-slop audit flagged inconsistent bylines (four variants across
 * 70 posts) and a real name emitted as a schema.org Person with no bio,
 * credentials, or profile link — plus an AuthorCard that manufactured a
 * social-follow widget for brand bylines. This registry fixes that: every
 * byline resolves to a real, honestly-described identity with verifiable links.
 *
 * SOURCING RULE: every field below is verified from the author's own site
 * (https://calebleigh.me) or is demonstrably true (he built and maintains
 * TeslaLockSound). Nothing is invented. Do NOT add credentials, social
 * handles, or expertise that aren't independently verifiable — that is exactly
 * the provenance slop this registry exists to prevent.
 *
 * Rules:
 * - Use a real human author (Caleb Leigh) for posts he authored.
 * - Use the editorial team identity for collaboratively-maintained content.
 * - Only emit schema.org Person markup for a real person with `sameAs` links.
 */

export interface Author {
  /** Stable key + canonical byline string used in blog-data.ts `author` fields. */
  slug: string
  name: string
  role: string
  /** Short bio for the AuthorCard. */
  bio: string
  /** Longer bio for the author page / about contexts. */
  longBio?: string
  /** Repo-relative avatar, or undefined for the team identity (renders monogram). */
  avatarUrl?: string
  /** Canonical profile URL (used as schema.org Person.url). */
  url: string
  /** Verifiable identities for schema.org `sameAs`. Verified on calebleigh.me. */
  sameAs: string[]
  twitterUrl?: string
  linkedinUrl?: string
  /** Only domains the author genuinely works in. Used for schema.org knowsAbout. */
  knowsAbout: string[]
  /** True only for a real person (gates schema.org Person emission). */
  isPerson: boolean
}

export const AUTHORS = {
  'caleb-leigh': {
    slug: 'Caleb Leigh',
    name: 'Caleb Leigh',
    role: 'Founder, TeslaLockSound',
    bio: 'Denver-based product builder and the founder of TeslaLockSound. He built the site after the custom lock-sound pages he found were low-quality or sketchy, so he made the one he wanted.',
    longBio:
      'Caleb Leigh is a Denver-based product builder and the founder of TeslaLockSound. He holds a computer science degree and spent years working on cybersecurity at Fortune 500 companies before building his own products at the intersection of design and AI. He started TeslaLockSound as a personal project: the custom lock-sound sites he found were "low quality, poorly edited, or just looked like a virus site," so he built the one he wanted instead.',
    avatarUrl: '/images/authors/caleb-leigh.png',
    url: 'https://calebleigh.me',
    // Verified on calebleigh.me. No public GitHub profile or email is published
    // there, so none is asserted here.
    sameAs: ['https://calebleigh.me', 'https://x.com/askcalebleigh', 'https://www.linkedin.com/in/calebleigh/'],
    twitterUrl: 'https://x.com/askcalebleigh',
    linkedinUrl: 'https://www.linkedin.com/in/calebleigh/',
    // Honest, demonstrable scope: he built the largest free Tesla lock-sound
    // library + a browser-based chime converter. No "audio engineer" claim —
    // that is not corroborated on his own site.
    knowsAbout: ['Tesla lock sounds', 'Tesla Boombox', 'custom Tesla chimes', 'product development', 'web development'],
    isPerson: true,
  },
  'tesla-lock-sound-team': {
    slug: 'Tesla Lock Sound Team',
    name: 'Tesla Lock Sound Team',
    role: 'Editorial',
    bio: 'The TeslaLockSound editorial team curates the sound library and keeps the install guides accurate as Tesla’s software changes.',
    url: 'https://teslalocksound.com/about',
    sameAs: ['https://teslalocksound.com'],
    knowsAbout: ['Tesla lock sounds', 'Tesla Boombox', 'custom Tesla chimes'],
    isPerson: false,
  },
} satisfies Record<string, Author>

export type AuthorKey = keyof typeof AUTHORS

const DEFAULT_AUTHOR: AuthorKey = 'tesla-lock-sound-team'

/**
 * Resolve any legacy byline string to a canonical Author.
 * Legacy variants seen in blog-data.ts: 'TeslaLockSound Team', 'TeslaLockSound
 * Editorial', 'Tesla Lock Sound' → team; 'Caleb Leigh' → caleb-leigh.
 */
export function getAuthor(nameOrSlug: string | undefined): Author {
  if (!nameOrSlug) return AUTHORS[DEFAULT_AUTHOR]
  const v = nameOrSlug.trim().toLowerCase()
  if (v.includes('caleb')) return AUTHORS['caleb-leigh']
  return AUTHORS['tesla-lock-sound-team']
}
